import { t } from '@/features/i18n/utils';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderUserMenu } from '@/ui/components/header';
import { H1 } from '@/ui/components/heading';
import { MainContainer, MainHeader, MainScrollArea } from '@/ui/components/main';
import { SquareLock01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { useAuth } from '@/features/auth';
import { UserRole } from '@/features/user/constants';
import { ssoConfigQuery } from '@/features/sso/queries';
import {
  useRemoveSsoDomain,
  useSetDomainAutoProvision,
  useSetSsoEnforced,
} from '@/features/sso/mutations';
import { AddSsoDomain } from '@/features/sso/components/add-sso-domain';
import { IpAllowlistCard } from '@/features/network/components/ip-allowlist-card';
import { SessionTimeoutCard } from '@/features/network/components/session-timeout-card';
import { Button } from '@/ui/components/button';
import { Card } from '@/ui/components/card';
import { Switch } from '@/ui/components/switch';
import { toastManager } from '@/ui/components/toast';

export const Route = createFileRoute('/_authed/settings/sso')({
  component: RouteComponent,
  loader: async ({ context }) => {
    if (context.auth.user.organizationRole !== UserRole.ORGANIZATION_ADMIN) throw notFound();
    await context.queryClient.query({
      ...ssoConfigQuery(context.auth.user.organizationId),
      staleTime: 'static',
    });
  },
});

function RouteComponent() {
  const auth = useAuth();
  const { data: config } = useSuspenseQuery(ssoConfigQuery(auth.user.organizationId));

  const removeDomain = useRemoveSsoDomain();
  const setEnforced = useSetSsoEnforced();
  const setAutoProvision = useSetDomainAutoProvision();

  const verifiedDomainCount = config.domains.filter((d) => d.verifiedAt).length;
  const hasVerifiedDomain = verifiedDomainCount > 0;

  return (
    <>
      <Header>
        <HeaderBackButton to="/" />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={SquareLock01Icon} />
            {t({ en: 'Single sign-on', nb: 'Enkeltpålogging' })}
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderUserMenu />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>{t({ en: 'Single sign-on', nb: 'Enkeltpålogging' })}</H1>
        </MainHeader>
        <MainContainer className="max-w-xl space-y-6">
          <Card className="space-y-6 p-4">
            <p className="text-muted text-sm">
              {t({
                en: "Require your team to sign in through your organisation's Microsoft Entra tenant.",
                nb: 'Krev at teamet ditt logger inn via organisasjonens Microsoft Entra-tenant.',
              })}
            </p>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-sm">{t({ en: 'Enforce SSO', nb: 'Påtving SSO' })}</p>
                <p className="text-muted text-sm mt-2">
                  {t({
                    en: 'Block password login for your domains — users must sign in via SSO.',
                    nb: 'Blokker passordinnlogging for domenene dine – brukere må logge inn via SSO.',
                  })}
                </p>
              </div>
              <Switch
                checked={config.enforced}
                disabled={setEnforced.isPending}
                onCheckedChange={(checked) => {
                  if (checked && !hasVerifiedDomain)
                    return toastManager.add({
                      type: 'error',
                      title: t({
                        en: 'Add a domain and sign in via SSO once to verify it before you can enforce.',
                        nb: 'Legg til et domene og logg inn via SSO én gang for å verifisere det før du kan påtvinge.',
                      }),
                    });
                  setEnforced.mutate(checked);
                }}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <p className="font-medium text-sm">{t({ en: 'Domains', nb: 'Domener' })}</p>
                <AddSsoDomain />
              </div>
              {config.domains.length === 0 ? (
                <p className="text-muted text-sm">
                  {t({ en: 'No domains added yet.', nb: 'Ingen domener lagt til ennå.' })}
                </p>
              ) : (
                config.domains.map((domain) => (
                  <div key={domain.id} className="rounded-md border border-border p-3 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium">{domain.emailDomain}</p>
                        <p className="text-muted text-xs break-all">{domain.entraTenantId}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span
                          data-verified={domain.verifiedAt ? '' : undefined}
                          className="text-xs text-muted data-verified:text-success-11"
                        >
                          {domain.verifiedAt
                            ? t({ en: 'Verified', nb: 'Verifisert' })
                            : t({ en: 'Pending verification', nb: 'Venter på verifisering' })}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={removeDomain.isPending}
                          onClick={() => {
                            if (config.enforced && domain.verifiedAt && verifiedDomainCount <= 1)
                              return toastManager.add({
                                type: 'error',
                                title: t({
                                  en: 'Disable SSO enforcement before removing the last verified domain.',
                                  nb: 'Slå av SSO-påtving før du fjerner det siste verifiserte domenet.',
                                }),
                              });
                            removeDomain.mutate(domain.id);
                          }}
                        >
                          {t({ en: 'Remove', nb: 'Fjern' })}
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm">
                          {t({
                            en: 'Auto-join users from this tenant without an invite',
                            nb: 'Slipp inn brukere fra denne tenanten uten invitasjon',
                          })}
                        </p>
                        <p className="text-muted text-xs mt-0.5">
                          {t({
                            en: 'Applies to SSO sign-ins only — users signing in with email and password are never auto-joined.',
                            nb: 'Gjelder kun SSO-innlogging – brukere som logger inn med e-post og passord blir aldri lagt til automatisk.',
                          })}
                        </p>
                      </div>
                      <Switch
                        className="shrink-0"
                        size="sm"
                        checked={domain.autoProvision}
                        disabled={setAutoProvision.isPending}
                        onCheckedChange={(checked) =>
                          setAutoProvision.mutate({
                            domainId: domain.id,
                            autoProvision: checked,
                          })
                        }
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
          <IpAllowlistCard />
          <SessionTimeoutCard />
        </MainContainer>
      </MainScrollArea>
    </>
  );
}
